<template>
  <dashboard-block
    title="Заказы по дням"
    :caption="caption"
    :loading="loading"
    :missing="!block"
    :min-height="chartHeight + 67"
    skeleton="image"
  >
    <div
      v-if="hasOrders"
      class="dashboard-daily"
      :style="{ '--dashboard-daily-today': todayColor }"
    >
      <vue-apex-charts
        type="bar"
        :height="chartHeight"
        :options="options"
        :series="series"
      ></vue-apex-charts>

      <div class="d-flex flex-wrap text-caption text--secondary">
        <span
          v-for="item in legend"
          :key="item.name"
          class="d-inline-flex align-center me-4"
        >
          <span
            class="dashboard-daily__swatch me-2"
            :style="item.style"
          ></span>
          {{ item.name }}
        </span>
      </div>
    </div>

    <div
      v-else
      class="text--secondary"
    >
      За эти дни заказов ещё не было.
    </div>
  </dashboard-block>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import DashboardBlock from '@/views/dashboard/dashboard-block'
import { money, MONTHS_OF } from '@/helpers'
import { toCountString, toPeriodString, toShortWeekdayDayString } from '@/helpers/dashboard'

const CHART_HEIGHT = 250

const WEEKEND_COLOR = { light: '#eb6834', dark: '#d95926' }

export default {
  name: 'DashboardDaily',
  components: { DashboardBlock, VueApexCharts },
  props: {
    block: { type: Array, default: null },

    today: { type: String, default: null },
    loading: { type: Boolean, default: false },
  },
  data: () => ({
    chartHeight: CHART_HEIGHT,
  }),
  computed: {
    days() {
      return (this.block || []).map(day => ({ ...day, isToday: day.date === this.today }))
    },
    hasOrders() {
      return this.days.some(day => day.orders > 0)
    },
    caption() {
      const { days } = this

      return days.length ? `${days.length} дн.: ${toPeriodString(days[0].date, days[days.length - 1].date)}` : ''
    },
    weekendColor() {
      return WEEKEND_COLOR[this.$vuetify.theme.dark ? 'dark' : 'light']
    },
    todayColor() {
      const today = this.days.find(day => day.isToday)

      return today && today.isWeekend ? this.weekendColor : this.$vuetify.theme.currentTheme.primary
    },
    series() {
      const row = belongs => ({ data: this.days.map(day => (belongs(day) ? day.orders || 0 : 0)) })

      return [
        row(day => !day.isToday && !day.isWeekend),
        row(day => !day.isToday && day.isWeekend),
        row(day => day.isToday),
      ]
    },
    legend() {
      const { primary } = this.$vuetify.theme.currentTheme
      const hatch = `repeating-linear-gradient(135deg, ${this.todayColor} 0 2px, transparent 2px 4px)`

      return [
        { name: 'Будни', style: { background: primary } },
        { name: 'Выходные', style: { background: this.weekendColor } },
        { name: 'Сегодня, день не закончен', style: { background: hatch, boxShadow: `inset 0 0 0 1px ${this.todayColor}` } },
      ].filter((item, index) => this.series[index].data.some(orders => orders > 0))
    },
    options() {
      const { days } = this

      return {
        colors: [this.$vuetify.theme.currentTheme.primary, this.weekendColor, this.todayColor],
        chart: {
          stacked: true,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          fontFamily: 'inherit',
        },
        plotOptions: {
          bar: { columnWidth: '70%', borderRadius: 3 },
        },
        fill: {
          type: ['solid', 'solid', 'pattern'],
          opacity: 1,
          pattern: {
            style: 'slantedLines', width: 5, height: 5, strokeWidth: 2,
          },
        },
        states: {
          hover: { filter: { type: 'darken', value: 0.85 } },
          active: { filter: { type: 'none' } },
        },
        stroke: { show: false },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: {
          strokeDashArray: 4,
          padding: { left: 4, right: 4 },
        },
        xaxis: {
          categories: days.map(day => day.date),
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
          labels: {
            rotate: 0,
            hideOverlappingLabels: false,
            style: { fontSize: '11px' },

            formatter: value => this.weekLabel(value),
          },
        },
        yaxis: {
          tickAmount: 4,
          labels: {
            style: { fontSize: '11px' },
            formatter: value => toCountString(Math.round(value)),
          },
        },
        tooltip: {
          theme: this.$vuetify.theme.dark ? 'dark' : 'light',
          shared: true,
          intersect: false,
          custom: ({ dataPointIndex }) => this.tooltip(days[dataPointIndex]),
        },
      }
    },
  },
  methods: {
    weekLabel(value) {
      const day = this.days.find(item => item.date === value)

      if (!day || new Date(`${day.date}T00:00:00`).getDay() !== 1) return ''

      const [, month, date] = day.date.split('-')

      return `${Number(date)} ${MONTHS_OF[Number(month) - 1].slice(0, 3)}`
    },

    tooltip(day) {
      if (!day) return ''

      const lines = [
        `<div style="font-weight: 600">${toShortWeekdayDayString(day.date)}</div>`,
        day.isToday ? '<div>сегодня, день не закончен</div>' : '',
        `<div>Заказов: ${toCountString(day.orders)}</div>`,
        `<div>Выручка: ${money(day.revenue)}</div>`,
      ]

      return `<div style="padding: 8px 12px; font-size: 13px; line-height: 1.5">${lines.join('')}</div>`
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-daily {
          ::v-deep .apexcharts-series[data\:realIndex='2'] path:not([val='0']) {
    stroke: var(--dashboard-daily-today);
    stroke-width: 1px;
  }

  .dashboard-daily__swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }
}
</style>
